/**
 * Стиль липкой строки действий («название + Сохранить + История»).
 *
 * Одно место на оба виджета истории (`product-history`, `user-history`): строка
 * у них общая по смыслу, и разъехавшийся z-index чинился бы дважды — второй раз
 * уже после того, как дефект снова доехал до платформы.
 *
 * Почему z-index именно такой. antd рисует замороженную колонку таблицы как
 * `position: sticky; z-index: 2`, а её заголовок — `z-index: 3` (проверено в DOM
 * на живом дереве, а не по документации). Строка стояла на z-index 2, то есть
 * ровно вровень с ячейками, и при равенстве побеждает тот, кто ниже по документу —
 * таблица. Отсюда и симптом: прокрученная таблица прогноза рисовалась поверх поля
 * ввода. Слои antd для всплывающего (Drawer, Modal, Select) начинаются с 1000,
 * поэтому значение обязано остаться заметно НИЖЕ их и выше табличного хрома.
 *
 * Фон непрозрачный по той же причине: под строкой проезжает содержимое расчёта, и
 * прозрачная полоса читалась бы как наложение двух интерфейсов друг на друга.
 */

import type { CSSProperties } from 'react';

/**
 * Слой липкой строки зажат между двумя чужими числами, и оба проверены на живом:
 *
 * - СНИЗУ antd: замороженная колонка таблицы — `z-index: 2`, её заголовок — `3`.
 *   Строка обязана быть выше, иначе прокрученная таблица накрывает поле ввода.
 * - СВЕРХУ платформа: в её таблице стилей есть
 *   `.ant-cascader-dropdown, .ant-select-dropdown, .ant-drawer, .slick-dots { z-index: 7 !important }`
 *   (снято со stage 2026-08-24). `!important` из чужой таблицы стилей сильнее
 *   инлайнового `zIndex` у Drawer, поэтому наш сайдбар в embed получает 7, что бы
 *   мы ему ни передали. Строка со слоем 10 закрывала его шапку с заголовком и
 *   крестиком — ровно то, что было видно на платформе.
 *
 * Отсюда 5: выше табличного хрома, ниже всего, что платформа считает всплывающим.
 * Поднимать это число нельзя — оно сломает сайдбар; спорить с хостом за слой тоже:
 * его правило сильнее по построению.
 */
export const STICKY_TOOLBAR_Z_INDEX = 5;

/**
 * Слой сайдбара истории — задаётся ЯВНО, а не берётся из темы.
 *
 * Дефолт antd для всплывающего приходит из токена `zIndexPopupBase`, а antd в
 * embed — общий синглтон с хостом: значение токена задаёт платформа, и наш
 * сайдбар оказался ниже собственной липкой строки (шапка сайдбара с заголовком
 * и крестиком просто исчезала под её непрозрачной полосой — снимок с платформы
 * 2026-08-24). Число, выведенное из чужой темы, чинить нечем: оно меняется без
 * нашего участия.
 *
 * 1200 — то же значение, что у подтверждений в `Calculator` и `ProductHistory`.
 * ⚠️ В embed оно НЕ действует: платформа придавливает `.ant-drawer` до 7 правилом
 * с `!important`, а оно сильнее инлайнового стиля. Значение остаётся ради
 * standalone и ради явности — но рассчитывать на него в embed нельзя, и именно
 * поэтому наша липкая строка живёт ниже платформенной семёрки, а не выше.
 */
export const HISTORY_DRAWER_Z_INDEX = 1200;

export function stickyToolbarStyle(background: string, paddingBlock: number): CSSProperties {
  return {
    position: 'sticky',
    top: 0,
    zIndex: STICKY_TOOLBAR_Z_INDEX,
    background,
    paddingBlock,
    display: 'flex',
    flexWrap: 'wrap',
    // Поле, «Сохранить» и «История» идут одной группой и прижаты ВЛЕВО: сайдбар
    // истории выезжает справа и накрыл бы ровно эти элементы.
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 8,
  };
}
